/** Proyectos del tablero: grid con color, archivar/restaurar, borrar en cascada. */
import { useEffect, useMemo, useState } from 'react';
import { Archive, ArchiveRestore, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useTaskMutations } from '@/hooks/useInternalTasks';
import { DEFAULT_PROJECT_COLOR, type ProjectColor, type Task, type TaskProject } from '@/../../shared/internal-tasks';
import { ColorPicker } from './task-fields';
import { EmptyState, fmtDateTime } from '../shared';
import { useTranslation } from 'react-i18next';

export function ProjectsView({ projects, tasks }: { projects: TaskProject[]; tasks: Task[] }) {
  const { t } = useTranslation();
  const { updateProject, deleteProject } = useTaskMutations();
  const [tab, setTab] = useState<'active' | 'archived'>('active');
  const [editing, setEditing] = useState<TaskProject | null | 'new'>(null);
  const [toDelete, setToDelete] = useState<TaskProject | null>(null);

  const counts = useMemo(() => {
    const m = new Map<string, { total: number; open: number }>();
    for (const t of tasks) {
      if (t.parentTaskId) continue;
      const c = m.get(t.projectId) ?? { total: 0, open: 0 };
      c.total++;
      if (t.status !== 'completed') c.open++;
      m.set(t.projectId, c);
    }
    return m;
  }, [tasks]);

  const active = projects.filter((p) => p.status === 'active');
  const archived = projects.filter((p) => p.status === 'archived');
  const shown = (tab === 'active' ? active : archived).slice().sort((a, b) => a.name.localeCompare(b.name));
  const deleteTaskCount = toDelete ? tasks.filter((task) => task.projectId === toDelete.projectId).length : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={tab} onValueChange={(v) => setTab(v as 'active' | 'archived')}>
          <TabsList>
            <TabsTrigger value="active">{t('internal.projectsview.activos')}{active.length})</TabsTrigger>
            <TabsTrigger value="archived">{t('internal.projectsview.archivados')}{archived.length})</TabsTrigger>
          </TabsList>
        </Tabs>
        <Button size="sm" className="ml-auto" onClick={() => setEditing('new')}>
          <Plus className="mr-1 h-4 w-4" /> {t('internal.projectsview.nuevo_proyecto')}
        </Button>
      </div>

      {shown.length === 0 ? (
        <EmptyState
          title={tab === 'active' ? t('internal.projectsview.todavia_no_hay_proyectos') : t('internal.projectsview.no_hay_proyectos_archivados')}
          description={tab === 'active' ? t('internal.projectsview.un_proyecto_agrupa_tareas_del_tablero') : undefined}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((p) => {
            const c = counts.get(p.projectId) ?? { total: 0, open: 0 };
            return (
              <Card key={p.projectId} className="relative overflow-hidden p-4 pl-5">
                <span className="absolute inset-y-0 left-0 w-1.5" style={{ backgroundColor: p.color }} />
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{p.name}</p>
                    {p.description && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{p.description}</p>}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" aria-label={t('common.a11y.more_actions')} className="h-7 w-7 shrink-0"><MoreHorizontal className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditing(p)}><Pencil className="mr-2 h-4 w-4" /> Editar</DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => updateProject.mutate({ projectId: p.projectId, status: p.status === 'active' ? 'archived' : 'active' })}
                      >
                        {p.status === 'active'
                          ? <><Archive className="mr-2 h-4 w-4" /> Archivar</>
                          : <><ArchiveRestore className="mr-2 h-4 w-4" /> Restaurar</>}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setToDelete(p)}>
                        <Trash2 className="mr-2 h-4 w-4" /> Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant={p.status === 'active' ? 'default' : 'secondary'}>
                    {p.status === 'active' ? t('internal.projectsview.activo') : t('internal.projectsview.archivado')}
                  </Badge>
                  <span>{t('internal.projectsview.n_tareas_m_abiertas', { n: c.total, m: c.open })}</span>
                  <span className="ml-auto">{fmtDateTime(p.updatedAt)}</span>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <ProjectDialog project={editing} onClose={() => setEditing(null)} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('internal.projectsview.eliminar_name', { name: toDelete?.name })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('internal.projectsview.se_eliminan_tambien_sus')} {deleteTaskCount} {t('internal.projectsview.tareas_y_subtareas_esta_accion_no')}
              {deleteTaskCount > 0 && ` ${t('internal.projectsview.si_queres_conservarlas_archiva_el_proyecto')}`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { if (toDelete) deleteProject.mutate({ projectId: toDelete.projectId }); setToDelete(null); }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ProjectDialog({ project, onClose }: { project: TaskProject | null | 'new'; onClose: () => void }) {
  const { t } = useTranslation();
  const { createProject, updateProject } = useTaskMutations();
  const open = project !== null;
  const isNew = project === 'new';
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string>(DEFAULT_PROJECT_COLOR);

  useEffect(() => {
    if (!open) return;
    setName(isNew ? '' : project.name);
    setDescription(isNew ? '' : project.description ?? '');
    setColor(isNew ? DEFAULT_PROJECT_COLOR : project.color);
  }, [open, isNew, project]);

  const pending = createProject.isPending || updateProject.isPending;
  const canSave = name.trim().length > 0 && !pending;

  const submit = () => {
    if (!canSave) return;
    const payload = { name: name.trim(), description: description.trim(), color: color as ProjectColor };
    if (isNew) createProject.mutate(payload, { onSuccess: onClose });
    else if (project) updateProject.mutate({ projectId: project.projectId, ...payload }, { onSuccess: onClose });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{isNew ? t('internal.projectsview.nuevo_proyecto') : t('internal.projectsview.editar_proyecto')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Nombre</Label>
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
              placeholder={t('internal.projectsview.ej_brain_people_flow_inventario')}
              maxLength={80}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('internal.projectsview.descripcion')}</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={500} placeholder="Opcional" />
          </div>
          <div className="space-y-1.5">
            <Label>Color</Label>
            <ColorPicker value={color} onChange={setColor} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={!canSave}>{pending ? t('internal.projectsview.guardando') : isNew ? t('internal.projectsview.crear_proyecto') : t('common.ui.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
